import type { AppState, AthleteProfile, BodyMeasurement, CoachMessage, DietCheckIn, Preferences, WorkoutSession, WorkoutTemplate } from '../domain/types'

export interface CloudState {
  version: 1
  updatedAt: string
  profile: AthleteProfile
  dietCheckIns: DietCheckIn[]
  measurements: Omit<BodyMeasurement, 'photo'>[]
  sessions: WorkoutSession[]
  preferences: Preferences
  templates?: WorkoutTemplate[]
  coachMessages?: CoachMessage[]
  exerciseCatalog?: Array<{ id: string; name: string; category: string; equipment: string }>
  exerciseOverrides: Array<{ id: string; increment: number; preferredWeight?: number; restSeconds: number; videoUrl?: string }>
}

const endpoint = () => new URL('/api/state', window.location.origin).href

export async function loadCloudState(): Promise<CloudState | undefined> {
  const response = await fetch(endpoint(), { credentials: 'same-origin', cache: 'no-store' })
  if (!response.ok) throw new Error(`cloud_load_${response.status}`)
  const document = await response.json() as { state?: CloudState | null }
  return document.state?.version === 1 ? document.state : undefined
}

export async function saveCloudState(state: CloudState): Promise<void> {
  const response = await fetch(endpoint(), {
    method: 'PUT',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(state),
  })
  if (!response.ok) throw new Error(`cloud_save_${response.status}`)
}

export function toCloudState(state: AppState): CloudState {
  return {
    version: 1,
    updatedAt: state.updatedAt,
    profile: state.profile,
    dietCheckIns: state.dietCheckIns,
    measurements: state.measurements.map(({ photo: _photo, ...measurement }) => measurement),
    sessions: state.sessions,
    preferences: state.preferences,
    templates: state.templates,
    coachMessages: state.coachMessages,
    exerciseCatalog: state.exercises.map(({ id, name, category, equipment }) => ({ id, name, category, equipment })),
    exerciseOverrides: state.exercises.map(({ id, increment, preferredWeight, restSeconds, videoUrl }) => ({ id, increment, preferredWeight, restSeconds, videoUrl })),
  }
}

export function mergeCloudState(local: AppState, cloud: CloudState): AppState {
  const overrides = new Map(cloud.exerciseOverrides.map((exercise) => [exercise.id, exercise]))
  const localPhotos = new Map(local.measurements.filter((measurement) => measurement.photo).map((measurement) => [measurement.id, measurement.photo]))
  const cloudIds = new Set(cloud.measurements.map((measurement) => measurement.id))
  return {
    ...local,
    updatedAt: cloud.updatedAt,
    profile: cloud.profile,
    dietCheckIns: cloud.dietCheckIns,
    sessions: cloud.sessions,
    preferences: { ...local.preferences, ...cloud.preferences },
    templates: cloud.templates?.length ? cloud.templates : local.templates,
    coachMessages: cloud.coachMessages ?? local.coachMessages,
    measurements: [
      ...cloud.measurements.map((measurement) => ({ ...measurement, photo: localPhotos.get(measurement.id) })),
      ...local.measurements.filter((measurement) => measurement.photo && !cloudIds.has(measurement.id)),
    ],
    exercises: local.exercises.map((exercise) => ({ ...exercise, ...overrides.get(exercise.id) })),
  }
}
